/**
 * KuralIhlalListesi.jsx — Fuar kural ihlallerinin (KuralIhlaliDTO) tek render noktası.
 *
 * Aynı şekil ÜÇ yerden geliyor (bkz. docs/guides/FE-fuar-kural-sistemi-guide.md Part 4):
 *   · POST /declare'in 400 gövdesi          → beyan reddedildi, hiçbir şey kaydedilmedi
 *   · POST /declaration/validate            → kuru çalıştırma, her zaman 200
 *   · projeBeyani.violations[]              → her okumada yeniden türetilen liste
 * Bir kez yazıldı, üç yerde de kullanılıyor.
 *
 * ⚠️ `message` TÜRKÇE ve KENDİ BAŞINA TAM — olduğu gibi basılır. `code` asla
 *    dekoratöre gösterilmez, sadece ikon/renk seçmek için var.
 * ⚠️ HEPSİNİ gösterin, ilkini değil: backend kasıtlı olarak tüm ihlalleri döner,
 *    tek tanesini basmak dört düzeltmeyi dört tura çevirir. (`lockReason` da yalnızca
 *    ilkinin tek kelimelik özetidir — gerçek cevap bu liste.)
 *
 * ÜÇ SEVİYE (üçüncüsü 2026-08-28'de eklendi):
 *   ERROR     reddedildi, kaydedilmedi (ya da kaydedildiyse projeyi kilitliyor).
 *   WARNING   kabul edildi ve KAYDEDİLDİ, yanında gösteriliyor. Serbest kW hakkının
 *             aşılması para demek, kural ihlali değil — o yüzden akışı durdurmuyor.
 *   PERMITTED ONAYLI BİR ÖZEL İZNİN temizlediği bir ERROR.
 *
 * ⚠️ PERMITTED "düzeldi" DEĞİLDİR ve öyle basılmaz. İhlal gerçek, mesajı kelimesi
 *    kelimesine korunuyor, listeden çıkarılmıyor; sadece artık kilitlemiyor.
 *    5,00 m'lik salondaki 5,50 m'lik stand "yasal görünen bir sayı" değil, "birinin
 *    imzaladığı bir istisna" olarak okunmalı — bu yüzden yeşil değil MAVİ/bilgi
 *    kutusunda, kendi başlığıyla ve izin numarasıyla gösteriliyor.
 *
 * Saf sunum bileşeni: servis çağrısı yok, kendi state'i yok.
 */
import React from "react";
import { Alert, Badge } from "reactstrap";
import { AlertTriangle, XCircle, Info, ShieldCheck } from "lucide-react";
import { VIOLATION_SEVERITY, HMAX_SOURCE_LABELS } from "../../services/fuarKuralService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// severity yoksa ERROR varsayılır — eksik alan yüzünden bir ihlali sessizce
// "bilgilendirme"ye düşürmek yanlış yöndeki hata olurdu.
const severityOf = (v) => v?.severity ?? VIOLATION_SEVERITY.ERROR;
const isError = (v) => severityOf(v) === VIOLATION_SEVERITY.ERROR;
const isPermitted = (v) => severityOf(v) === VIOLATION_SEVERITY.PERMITTED;
const isWarning = (v) => severityOf(v) === VIOLATION_SEVERITY.WARNING;

/** Sayıyı Türkçe ondalıkla, gereksiz sıfır basmadan. */
function fmtNum(v) {
  if (v == null) return null;
  const n = Number(v);
  return (Number.isInteger(n) ? String(n) : n.toFixed(2)).replace(".", ",");
}

/**
 * "Ahşap standlar için — sınır 24 m², beyan 18 m²" gibi ikinci satır. message zaten
 * tam cümle olduğu için bu SADECE sayıları/kaynağı hızlı taramak isteyen incelemeci
 * için; kaynak yoksa hiç basılmaz.
 */
function sourceLine(v) {
  const parts = [];
  if (v.sourceStandTypeName) parts.push(v.sourceStandTypeName);
  else if (v.source && HMAX_SOURCE_LABELS[v.source]) parts.push(HMAX_SOURCE_LABELS[v.source]);
  const limit = fmtNum(v.limitValue);
  const actual = fmtNum(v.actualValue);
  if (limit != null) parts.push(i18n.t("components:kuralIhlal.sinir", { limit }));
  if (actual != null) parts.push(`beyan ${actual}`);
  return parts.length ? parts.join(" · ") : null;
}

/** Tek bir ihlal satırı — bir inputun hemen altında da kullanılabilir. */
export function KuralIhlalSatiri({ violation, className = "" }) {
  const { t } = useTranslation("components");
  if (!violation) return null;
  const permitted = isPermitted(violation);
  const err = isError(violation);
  const Icon = permitted ? ShieldCheck : err ? XCircle : AlertTriangle;
  const tone = permitted ? "text-info" : err ? "text-danger" : "text-warning";
  const line = sourceLine(violation);
  return (
    <div className={`small d-flex align-items-start gap-1 ${tone} ${className}`}>
      <Icon size={13} className="flex-shrink-0 mt-1" />
      <span>
        {violation.message}
        {/* İzin numarası satırın kendisinde: "bu neden hâlâ listede?" sorusunun cevabı. */}
        {permitted && (
          <Badge color="info" className="ms-2 align-middle">
            {t("kuralIhlal.ozelIzin")}{violation.ozelIzinId != null ? ` #${violation.ozelIzinId}` : ""}
          </Badge>
        )}
        {line && <span className="text-muted d-block">{line}</span>}
      </span>
    </div>
  );
}

/**
 * Tam liste — hata / izinli / uyarı ayrı kutularda. Hatalar önce: biri akışı
 * durduruyor, diğerleri durdurmuyor; aynı kutuya koymak hepsini eşit ağırlıkta
 * gösterirdi.
 *
 * props:
 *  violations  — KuralIhlaliDTO[]
 *  fields      — sadece bu `field` değerlerini göster (input altı kullanım); yoksa hepsi
 *  hideFields  — bu `field` değerlerini ATLA (inputların altında zaten gösterilenler)
 *  title       — hata kutusunun başlığı
 */
export default function KuralIhlalListesi({
  violations, fields = null, hideFields = null, title = i18n.t("components:kuralIhlal.beyanFuarKurallarinaUymuyor"),
  className = "",
}) {
  const { t } = useTranslation("components");
  const all = Array.isArray(violations) ? violations : [];
  const shown = all.filter((v) => {
    if (fields && !fields.includes(v.field)) return false;
    if (hideFields && hideFields.includes(v.field)) return false;
    return true;
  });
  if (shown.length === 0) return null;

  const errors = shown.filter(isError);
  const permitted = shown.filter(isPermitted);
  const warnings = shown.filter(isWarning);

  return (
    <div className={className}>
      {errors.length > 0 && (
        <Alert color="danger" className="mb-2">
          <div className="d-flex align-items-center gap-2 mb-2">
            <XCircle size={16} className="flex-shrink-0" />
            <span className="fw-semibold">{title}</span>
            <Badge color="danger">{errors.length}</Badge>
          </div>
          <div className="d-flex flex-column gap-2">
            {errors.map((v, i) => (
              <KuralIhlalSatiri key={`${v.code}-${v.field}-${i}`} violation={v} />
            ))}
          </div>
        </Alert>
      )}
      {permitted.length > 0 && (
        <Alert color="info" className="mb-2">
          <div className="d-flex align-items-center gap-2 mb-2">
            <ShieldCheck size={16} className="flex-shrink-0" />
            {/* "Özel izinli" — "uygun" değil. Kural hâlâ çiğneniyor, sadece imzalı. */}
            <span className="fw-semibold">{t("kuralIhlal.ozelIzinleKabulEdilenMaddeler")}</span>
            <Badge color="info">{permitted.length}</Badge>
          </div>
          <div className="d-flex flex-column gap-2">
            {permitted.map((v, i) => (
              <KuralIhlalSatiri key={`${v.code}-${v.field}-${i}`} violation={v} />
            ))}
          </div>
          <div className="text-muted small mt-2">
            {t("kuralIhlal.buMaddelerFuarKurallarinaAykiridir")}
          </div>
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert color="warning" className="mb-2">
          <div className="d-flex align-items-center gap-2 mb-2">
            <Info size={16} className="flex-shrink-0" />
            {/* Kasıtlı ayrı başlık: uyarı beyanı ENGELLEMEZ, kaydedilir. */}
            <span className="fw-semibold">{t("kuralIhlal.bilgilendirmeBeyaniEngellemez")}</span>
            <Badge color="warning">{warnings.length}</Badge>
          </div>
          <div className="d-flex flex-column gap-2">
            {warnings.map((v, i) => (
              <KuralIhlalSatiri key={`${v.code}-${v.field}-${i}`} violation={v} />
            ))}
          </div>
        </Alert>
      )}
    </div>
  );
}

/**
 * "Özel izin talep edilebilir mi?" — düğmeyi göstermeden ÖNCE sorulacak soru.
 * En az bir ERROR var ve o ERROR bir iznin temizleyebileceği türden.
 * ⚠️ `permittable` yalnızca ERROR üzerinde anlamlıdır; sınıflandırılmamış bir kod
 *    backend'de varsayılan olarak `true` sayılır (kurtarılabilir yön).
 */
export const hasPermittableError = (violations) =>
  (Array.isArray(violations) ? violations : [])
    .some((v) => isError(v) && v.permittable !== false);

/** İznin ASLA temizleyemeyeceği ihlaller — bunlar varsa talep hiç açılmaz. */
export const nonPermittableErrors = (violations) =>
  (Array.isArray(violations) ? violations : [])
    .filter((v) => isError(v) && v.permittable === false);
